import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiGetHfBasicMedDataPatientId } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { AddButton } from "../../../components/ui-kit/add-button";
import { MedicalPlotModel } from "../../../models/plotting";
import { useFetch } from "../../../utils/use-fetch";
import { MedData, MedDataItem } from "../common/med-data";
import { PatientSubpage } from "../common/patient-subpage";
import { PlotCanvas, PlotControls, usePlot } from "../common/plot";
import { AddVitalsDialog } from "./add-vitals-dialog";
import { BasicMedicalTable } from "./basic-medical-table";

export const BasicMedicalRoute = observer(function BasicMedicalRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [model] = useState(
    () => new MedData<MedDataItem>([], api.permissions, api.requireUserUUID()),
  );

  const [data, setData] = useState<MedDataItem[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { loading, refetch } = useFetch(
    async ({ isRefetch }) => {
      const response = await apiGetHfBasicMedDataPatientId({
        path: { patient_id: patient.patientId },
        meta: { useSessionCache: !isRefetch },
      });
      model.updateData(response.data.medical_data);
      setData(response.data.medical_data);
    },
    [patient.patientId],
  );

  const plot = usePlot(
    () =>
      new MedicalPlotModel(
        model,
        ["weight", "dry weight", "temperature", "respiratory rate"],
        "plot-canvas",
      ),
  );

  return (
    <PatientSubpage title={t`Basic medical data`} loading={loading}>
      <PlotCanvas model={plot} />

      <BasicMedicalTable
        filterActions={
          <>
            <AddButton onClick={() => setShowAddDialog(true)}>
              <Trans>Add basic medical data</Trans>
            </AddButton>
            <PlotControls model={plot} />
          </>
        }
        api={api}
        data={data}
        onInvalidated={refetch}
        patient={patient}
        persistState
      />

      {showAddDialog && (
        <AddVitalsDialog
          app={props.app}
          variant="basics"
          onClose={() => setShowAddDialog(false)}
          onSaved={refetch}
        />
      )}
    </PatientSubpage>
  );
});
